import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router";
import { Button, Divider, Modal, Spin, Steps } from "antd";
import { UploadPrice } from "../../components/UploadPrice/UploadPrice";
import { UploadPriceError } from "../../components/UploadPrice/UploadPriceError";
import { UploadPriceResult } from "../../components/UploadPrice/UploadPriceResult";
import {
  fetchPriceTemplates,
  createPriceImport,
  checkPriceImportStatus,
  checkActiveImport,
  deletePriceImport,
  getPriceValidationResult,
  getPriceValidationClosingResult,
  savePriceImport,
} from "../../store/slices/individualTariffs";
import { useAppDispatch, useAppSelector } from "../..";
import { fetchCurrenciesDic } from "../../store/slices/rtkDictionaries";
import { ImportPriceData, PriceImportStatus } from "../../lib/types";
import { priceImportStatuses, priceValidationCodes } from "../../lib/const";
import { useActiveModal, usePagination } from "../../lib/utils";
import { ReactComponent as Error } from "../../img/error.svg";
import { ReactComponent as Success } from "../../img/success.svg";
import { ReactComponent as Info } from "../../img/info.svg";
import { fetchOfferList } from "../../store/slices/offering/offering";

const codesTextLimit = 45;
const checkPriceImportStatusInterval = 2000;

type ActiveModal = "" | "importDeleted" | "tafiffUpdateDone" | "tafiffUpdateError";

// Текст лоадера
const loadingTitles: Partial<Record<PriceImportStatus, string>> = {
  [priceImportStatuses.importCreated]: "Выполняется процесс валидации",
  [priceImportStatuses.importValidateProcess]: "Выполняется процесс валидации",
  [priceImportStatuses.tafiffUpdateProcess]: "Выполняется сохраненение данных",
  [priceImportStatuses.importDeleteProcess]: "Выполняется отмена импорта",
};

/* ---------------------------------------------- Начало компонента ---------------------------------------------- */

export const UploadPriceContainer = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const params = useParams<{ accountId: string; agreementId: string }>();
  const accountId = Number(params.accountId);
  const agreementId = Number(params.agreementId);
  const { activeModal, setActiveModal } = useActiveModal<ActiveModal>("");

  const { priceValidationResult } = useAppSelector((state) => state.individualTariffs);

  const [loading, setLoading] = useState<{ condition: boolean; title?: string }>({
    condition: false,
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [activeImport, setActiveImport] = useState<{ id: number; statusId: PriceImportStatus }>();
  // console.log(activeImport);

  const timer = useRef<any>();

  const paginationOptions = usePagination({ page: 1, size: 10 });
  const closingPaginationOptions = usePagination({ page: 1, size: 10 });

  // Загрузка справочников
  useEffect(() => {
    dispatch(fetchPriceTemplates());
    dispatch(fetchCurrenciesDic());
    dispatch(fetchOfferList({ agreementId }));
  }, []);

  const checkingImportStatus = () => {
    if (!activeImport) return;
    const { id, statusId } = activeImport;
    setLoading({ condition: true, title: loadingTitles[statusId] });
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      dispatch(checkPriceImportStatus(id)).then(({ payload }) => {
        setActiveImport({
          id,
          statusId: payload.statusId,
        });
      });
    }, checkPriceImportStatusInterval);
  };

  // Проверка при входе наличия активного импорта
  useEffect(() => {
    dispatch(checkActiveImport(agreementId)).then(({ payload }) => {
      if (payload) {
        const { id, statusId } = payload;
        setActiveImport({
          id,
          statusId,
        });
      }
    });
  }, []);

  const stopCheckingImportStatus = () => {
    clearInterval(timer.current);
  };

  // Реакции на статусы импорта
  useEffect(() => {
    if (activeImport) {
      switch (activeImport.statusId) {
        case priceImportStatuses.importCreated:
        case priceImportStatuses.importValidateProcess:
          checkingImportStatus();
          break;
        case priceImportStatuses.importValidateError:
        case priceImportStatuses.importValidateDone:
          stopCheckingImportStatus();
          Promise.all([
            dispatch(
              getPriceValidationResult({
                importId: activeImport.id,
                page: paginationOptions.page - 1,
                size: paginationOptions.size,
              })
            ),
            dispatch(
              getPriceValidationClosingResult({
                importId: activeImport.id,
                page: closingPaginationOptions.page - 1,
                size: closingPaginationOptions.size,
              })
            ),
          ])
            .then(() => {
              setCurrentStep(1);
            })
            .finally(() => {
              setLoading({ condition: false });
            });
          break;
        case priceImportStatuses.tafiffUpdateProcess:
          checkingImportStatus();
          break;
        case priceImportStatuses.tafiffUpdateError:
          stopCheckingImportStatus();
          setLoading({ condition: false });
          setActiveModal("tafiffUpdateError");
          break;
        case priceImportStatuses.tafiffUpdateDone:
          stopCheckingImportStatus();
          setLoading({ condition: false });
          setActiveModal("tafiffUpdateDone");
          break;
      }
    }
  }, [activeImport]);

  const onChangePagination = (params: { page: number; size: number }) => {
    const { page, size } = params;
    paginationOptions.setPage(page);
    paginationOptions.setSize(size);
    if (activeImport) {
      dispatch(
        getPriceValidationResult({
          importId: activeImport.id,
          page: page - 1,
          size,
        })
      );
    }
  };

  const onChangeClosingPagination = (params: { page: number; size: number }) => {
    const { page, size } = params;
    closingPaginationOptions.setPage(page);
    closingPaginationOptions.setSize(size);
    if (activeImport) {
      dispatch(
        getPriceValidationClosingResult({
          importId: activeImport.id,
          page: page - 1,
          size,
        })
      );
    }
  };

  const uploadPriceSubmit = (importPriceData: ImportPriceData) => {
    setLoading({ condition: true, title: loadingTitles[priceImportStatuses.importCreated] });
    dispatch(checkActiveImport(agreementId))
      .then(({ payload }) => {
        if (payload) {
          Modal.warning({
            content:
              "Для договора существует активный импорт. Загрузить новый можно после завершения обработки.",
            onOk: () => {
              history.push(`/accounts/${accountId}/agreements/${agreementId}/tariffs`);
            },
          });
        } else {
          dispatch(createPriceImport(importPriceData))
            .then(({ payload }) => {
              setActiveImport({
                id: payload.id,
                statusId: payload.statusId,
              });
            })
            .catch(() => {
              setLoading({ condition: false });
            });
        }
      })
      .catch(() => {
        setLoading({ condition: false });
      });
  };

  const handleDeletePriceImport = () => {
    setLoading({
      condition: true,
      title: loadingTitles[priceImportStatuses.importDeleteProcess],
    });
    dispatch(deletePriceImport(activeImport?.id!))
      .then(() => {
        setCurrentStep(0);
        setActiveModal("importDeleted");
        setActiveImport(undefined);
      })
      .finally(() => {
        setLoading({ condition: false });
      });
  };

  const handleSavePriceImport = () => {
    if (!activeImport) return;
    setLoading({
      condition: true,
      title: loadingTitles[priceImportStatuses.tafiffUpdateProcess],
    });
    dispatch(savePriceImport(activeImport.id)).then(() => {
      setActiveImport({
        id: activeImport.id,
        statusId: priceImportStatuses.tafiffUpdateProcess,
      });
    });
  };

  const importDeletedModal = (
    <Modal
      centered
      visible
      width="298px"
      footer={[
        <Button
          key="1"
          style={{ width: "108px" }}
          onClick={() => {
            setActiveModal("");
          }}
        >
          Ок
        </Button>,
      ]}
      bodyStyle={{
        textAlign: "center",
      }}
      closable={false}
    >
      <Info
        style={{
          width: "24px",
        }}
      />
      <div>Импорт отменен</div>
    </Modal>
  );

  const successModal = (
    <Modal
      centered
      visible
      width="298px"
      footer={[
        <Button
          key="1"
          style={{ width: "108px" }}
          type="primary"
          onClick={() => {
            history.push(`/accounts/${accountId}/agreements/${agreementId}/tariffs`);
          }}
        >
          Ок
        </Button>,
      ]}
      bodyStyle={{
        textAlign: "center",
      }}
      closable={false}
    >
      <Success />
      <div>Данные обновлены</div>
    </Modal>
  );

  const errorModal = (
    <Modal
      centered
      visible
      width="298px"
      footer={[
        <Button key="1" style={{ width: "158px" }} onClick={handleDeletePriceImport}>
          Отменить импорт
        </Button>,
      ]}
      bodyStyle={{
        textAlign: "center",
      }}
      closable={false}
    >
      <Error />
      <div>Ошибка сохранения данных</div>
      <div>выполните повторную загрузку данных</div>
    </Modal>
  );

  const hasFormatError = useMemo(
    () =>
      priceValidationResult?.reduce((result, { message }) => {
        const hasError = message.some(
          ({ code }) =>
            code === priceValidationCodes.invalidFormatError ||
            code === priceValidationCodes.emptyFileldError
        );
        if (hasError) return true;
        return result;
      }, false),
    [priceValidationResult]
  );

  // let renderSection = 2;
  const renderSection = useMemo(() => {
    if (
      !activeImport ||
      activeImport.statusId === priceImportStatuses.importCreated ||
      activeImport.statusId === priceImportStatuses.importValidateProcess
    ) {
      return 0;
    }
    if (hasFormatError) return 1;
    return 2;
  }, [activeImport, hasFormatError]);

  return (
    <div>
      <h1>Загрузка файла</h1>
      <Divider />
      <Steps current={currentStep}>
        <Steps.Step title="Выбор файла" />
        <Steps.Step title="Результат валидации" />
      </Steps>
      <Divider style={{ marginBottom: "5px" }} />
      <Spin spinning={loading.condition} tip={loading.title}>
        {renderSection === 0 && (
          <UploadPrice
            uploadPriceSubmit={uploadPriceSubmit}
            accountId={accountId}
            agreementId={agreementId}
          />
        )}
        {renderSection === 1 && (
          <UploadPriceError
            handleDeletePriceImport={handleDeletePriceImport}
            codesTextLimit={codesTextLimit}
          />
        )}
        {renderSection === 2 && (
          <UploadPriceResult
            handleDeletePriceImport={handleDeletePriceImport}
            handleSavePriceImport={handleSavePriceImport}
            codesTextLimit={codesTextLimit}
            activeImportStatusId={activeImport?.statusId}
            paginationOptions={{
              page: paginationOptions.page,
              size: paginationOptions.size,
              onChange: onChangePagination,
            }}
            closingPaginationOptions={{
              page: closingPaginationOptions.page,
              size: closingPaginationOptions.size,
              onChange: onChangeClosingPagination,
            }}
          />
        )}
      </Spin>
      {activeModal === "importDeleted" && importDeletedModal}
      {activeModal === "tafiffUpdateError" && errorModal}
      {activeModal === "tafiffUpdateDone" && successModal}
    </div>
  );
};
